import { t } from '@glory/i18n';
import { stageIndex, WORK_KINDS, type WorkKind } from '@glory/sales/domain';
import {
  Button,
  Callout,
  cn,
  ContentSwap,
  EmptyState,
  InfoHint,
  Segments,
  Select,
  SkeletonRows,
} from '@glory/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Handshake, UserRoundCheck } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { useMemo } from 'react';

import { type PipelineSearch, parsePipelineSearch } from '../../../features/sales/model/filters.ts';
import { workKindLabel } from '../../../features/sales/model/labels.ts';
import { useListActions } from '../../../features/sales/runtime/actions.ts';
import { DealPanel } from '../../../features/sales/ui/deal-panel.tsx';
import { PipelineBoard } from '../../../features/sales/ui/pipeline-board.tsx';
import { ClosedSection, PostponedSection } from '../../../features/sales/ui/pipeline-extras.tsx';
import { PipelineTable } from '../../../features/sales/ui/pipeline-table.tsx';
import { SalesHeader } from '../../../features/sales/ui/sales-tabs.tsx';
import { useStaffOptions } from '../../../features/clients/ui/responsible.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { requirePermissionRoute, usePermissions } from '../../../lib/permissions.ts';

export const Route = createFileRoute('/_app/sales/pipeline')({
  validateSearch: (search: Record<string, unknown>) => parsePipelineSearch(search),
  beforeLoad: requirePermissionRoute('sales.use'),
  component: PipelinePage,
});

/**
 * «Воронка»: доска этапов или таблица, фильтры «Мои / Все / ответственный / вид работы», «Отложенные»
 * свёрнуты внизу, закрытые — по фильтру «Закрытые». Карточка сделки — панель справа (`?deal=`).
 */
function PipelinePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const listActions = useListActions();
  const staff = useStaffOptions();
  const { can } = usePermissions();
  const table = search.view === 'table';
  // «Без ответственного» видит тот, кто может назначать: у остальных ссылка на него открывает «Все».
  const canUnassigned = can('sales.read_all');
  const scope = search.scope === 'unassigned' && !canUnassigned ? undefined : search.scope;

  const pipeline = useQuery({
    ...orpc.sales.pipeline.list.queryOptions({
      input: {
        ...(scope === undefined ? {} : { scope }),
        ...(search.responsible === undefined ? {} : { responsibleId: search.responsible }),
        ...(search.work === undefined ? {} : { workKind: search.work }),
        ...(search.closed === undefined ? {} : { closed: true }),
      },
    }),
    placeholderData: keepPreviousData,
  });
  const page = pipeline.data;
  const filtered =
    scope !== undefined ||
    search.responsible !== undefined ||
    search.work !== undefined ||
    search.closed !== undefined;

  // Соседи по ↑↓ в панели: в порядке доски — этап за этапом.
  const order = useMemo(
    () =>
      page
        ? [...page.items]
            .sort(
              (a, b) =>
                (a.stage === null ? 99 : stageIndex(a.stage)) - (b.stage === null ? 99 : stageIndex(b.stage)),
            )
            .map((row): string => row.id)
        : [],
    [page],
  );
  const openIndex = order.indexOf(search.deal ?? '');

  const show = (id: string | undefined, replace: boolean) => {
    void navigate({ search: (prev) => ({ ...prev, deal: id }), replace });
  };
  const setFilter = (patch: Partial<PipelineSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };
  const resetFilters = () => {
    void navigate({ search: (prev) => ({ deal: prev.deal, view: prev.view }), replace: true });
  };

  const nothing = page?.total === 0 && page.postponed.length === 0 && page.closed.length === 0;
  // Что показано под фильтрами: смена вида («Доска / Таблица») и состояний — проявление без скачка высоты.
  const view = pipeline.isError
    ? 'error'
    : page === undefined
      ? 'loading'
      : nothing && !filtered
        ? 'empty'
        : table
          ? 'table'
          : 'board';

  return (
    <div className="relative flex min-h-full w-full flex-col gap-4 px-4 py-5 md:px-6">
      <SalesHeader />

      <div className="flex flex-wrap items-center gap-2">
        <Segments
          size="sm"
          aria-label={t('sales.tabPipeline')}
          value={scope ?? 'all'}
          options={[
            { value: 'all', label: t('sales.filters.all') },
            { value: 'mine', label: t('sales.filters.mine') },
            ...(canUnassigned
              ? [
                  {
                    value: 'unassigned' as const,
                    label:
                      (page?.unassigned ?? 0) > 0
                        ? `${t('sales.filters.unassigned')} · ${String(page?.unassigned ?? 0)}`
                        : t('sales.filters.unassigned'),
                  },
                ]
              : []),
          ]}
          onValueChange={(next) => {
            setFilter({ scope: next === 'all' ? undefined : next });
          }}
        />
        {scope === 'unassigned' ? (
          <InfoHint
            label={t('sales.filters.unassigned')}
            hint={{
              title: t('sales.filters.unassigned'),
              text: t('sales.hints.unassigned.text'),
              example: t('sales.hints.unassigned.example'),
            }}
          />
        ) : null}
        <Select<string>
          size="sm"
          aria-label={t('sales.fields.responsible')}
          placeholder={t('sales.filters.responsible')}
          className="w-full sm:w-48"
          value={search.responsible ?? null}
          options={staff.map((person) => ({ value: person.id, label: person.name }))}
          clearable
          onValueChange={(responsible) => {
            setFilter({ responsible: responsible ?? undefined });
          }}
        />
        <Select<WorkKind>
          size="sm"
          aria-label={t('sales.fields.workKind')}
          placeholder={t('sales.filters.workKind')}
          className="w-full sm:w-48"
          value={search.work ?? null}
          options={WORK_KINDS.map((kind) => ({ value: kind, label: workKindLabel(kind) }))}
          clearable
          onValueChange={(work) => {
            setFilter({ work: work ?? undefined });
          }}
        />
        <button
          type="button"
          aria-pressed={search.closed === true}
          onClick={() => {
            setFilter({ closed: search.closed === true ? undefined : true });
          }}
          className={cn(
            'rounded-full border-thick px-2.5 py-0.5 text-small font-semibold transition-colors',
            search.closed === true
              ? 'border-strong bg-strong text-on-strong'
              : 'border-border text-muted hover:text-text',
          )}
        >
          {t('sales.filters.closed')}
        </button>
        <Segments
          size="sm"
          className="ml-auto"
          aria-label={t('sales.view.label')}
          value={table ? 'table' : 'board'}
          options={[
            { value: 'board', label: t('sales.view.board') },
            { value: 'table', label: t('sales.view.table') },
          ]}
          onValueChange={(view) => {
            setFilter({ view: view === 'table' ? 'table' : undefined });
          }}
        />
      </div>

      <ContentSwap swapKey={view}>
        {pipeline.isError ? (
          <Callout className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1">{pipeline.error.message}</span>
            <Button
              size="sm"
              onClick={() => {
                void pipeline.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          </Callout>
        ) : page === undefined ? (
          <SkeletonRows rows={6} />
        ) : nothing && !filtered ? (
          <EmptyState
            icon={<Handshake />}
            title={t('sales.pipeline.emptyTitle')}
            description={t('sales.pipeline.emptyText')}
            action={
              <Button asChild variant="primary">
                <Link to="/sales/leads" search={{ create: true }}>
                  {t('sales.newLead')}
                </Link>
              </Button>
            }
          />
        ) : (
          <>
            {nothing ? (
              <EmptyState
                icon={scope === 'unassigned' ? <UserRoundCheck /> : <Handshake />}
                title={
                  scope === 'unassigned'
                    ? t('sales.pipeline.emptyUnassignedTitle')
                    : t('sales.pipeline.emptyFilteredTitle')
                }
                description={
                  scope === 'unassigned'
                    ? t('sales.pipeline.emptyUnassignedText')
                    : t('sales.pipeline.emptyFilteredText')
                }
                action={<Button onClick={resetFilters}>{t('sales.resetFilters')}</Button>}
              />
            ) : table ? (
              <PipelineTable
                items={page.items}
                loading={false}
                activeId={search.deal ?? null}
                onOpen={(row) => {
                  show(row.id, search.deal !== undefined);
                }}
                onMove={(row, stage) => {
                  void listActions.moveCard(row, stage);
                }}
                empty={<p className="px-3 py-6 text-small text-muted">{t('sales.pipeline.noOpen')}</p>}
              />
            ) : (
              <PipelineBoard
                page={page}
                activeId={search.deal ?? null}
                onOpen={(row) => {
                  show(row.id, search.deal !== undefined);
                }}
                onMove={(row, stage) => {
                  void listActions.moveCard(row, stage);
                }}
              />
            )}
            <PostponedSection
              rows={page.postponed}
              activeId={search.deal ?? null}
              onOpen={(row) => {
                show(row.id, search.deal !== undefined);
              }}
              onResume={(row) => {
                void listActions.resume(row);
              }}
            />
            {search.closed === true ? (
              <ClosedSection
                rows={page.closed}
                activeId={search.deal ?? null}
                onOpen={(row) => {
                  show(row.id, search.deal !== undefined);
                }}
              />
            ) : null}
          </>
        )}
      </ContentSwap>

      {/* Постоянный key: переход к соседней записи не закрывает и не открывает панель заново. */}
      <AnimatePresence initial={false}>
        {search.deal ? (
          <DealPanel
            key="panel"
            id={search.deal}
            onClose={() => {
              show(undefined, true);
            }}
            onPrevious={() => {
              const previous = order[openIndex - 1];
              if (previous) show(previous, true);
            }}
            onNext={() => {
              const next = order[openIndex + 1];
              if (next) show(next, true);
            }}
            onExpand={() => {
              void navigate({ to: '/sales/deals/$id', params: { id: search.deal ?? '' } });
            }}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
